import { motion } from 'framer-motion'
import shejzaImage from '../assets/shejza.png'

const About = () => {
  const experience = [
    {
      title: 'React Developer',
      company: 'ThriveCoin',
      location: 'United States',
      period: '07/03/2022 - Current',
      description: 'Contributed to building and maintaining ThriveProtocol\'s core applications and ecosystem using React, Redux, TypeScript, and TailwindCSS. Created reusable UI components, integrated Web3 functionality, and ensured pixel-perfect design implementation.',
      technologies: ['React', 'Redux', 'TypeScript', 'TailwindCSS', 'Web3.js', 'Cypress', 'Docker']
    },
    {
      title: 'Front-End Developer',
      company: 'Visme',
      location: 'United States',
      period: '25/05/2021 - 01/03/2022',
      description: 'Contributed to Visme project to scale system for next growth phases with React.js, TypeScript and the latest web standards. Coordinated with team members to refine and enhance user-requested features.',
      technologies: ['React', 'TypeScript', 'HTML5', 'CSS3']
    },
    {
      title: 'Software Engineer',
      company: 'Sogody',
      location: 'Pristine, Kosovo',
      period: '14/09/2020 - 24/06/2021',
      description: 'Manipulated and rearranged existing client website code using JavaScript. Conducted technical audits and provided technical feasibility and time estimations on test concepts.',
      technologies: ['JavaScript', 'HTML5', 'CSS3', 'jQuery', 'BEM', 'Sass']
    },
    {
      title: 'Web Developer',
      company: 'Apprenticeship Program Emelleme',
      location: 'Kosovo',
      period: '01/06/2019 - 31/12/2019',
      description: 'Meeting with the development team to discuss user interface ideas and applications. Reviewing application requirements and interface designs. Identifying web-based user interactions. Developing and implementing highly-responsive user interface components using React concepts. Troubleshooting interface software and debugging application codes. Monitoring and improving front-end performance. Documenting application changes and developing updates.',
      technologies: ['React Js', 'HTML5', 'CSS3', 'Bootstrap', 'JavaScript', 'BEM', 'Firebase']
    },
    {
      title: 'Fullstack Developer',
      company: 'Smart Bits GmbH',
      location: 'Prishtine, Kosovo',
      period: '12/06/2017 - 30/06/2020',
      description: 'Started as Fullstack Developer designing and developing new features, collaborating with UI/UX designers, and building reusable code. Progressed to Front-End Developer role, developing user-facing features using React.js, ensuring pixel-perfect implementation and optimizing applications for maximum speed and scalability.',
      technologies: ['React', 'Laravel', 'HTML5', 'CSS3', 'JavaScript', 'Bootstrap', 'jQuery', 'Ajax', 'MySQL', 'BEM', 'Sass']
    }
  ]

  const skills = {
    'Front-End Development': ['React.js', 'React Native', 'Redux', 'MobX', 'Easy Peasy', 'Zustand', 'TypeScript', 'JavaScript (ES6+)', 'HTML5', 'CSS3', 'Sass (SCSS)', 'Tailwind CSS', 'Bootstrap', 'jQuery', 'Ajax'],
    'Web3 & Blockchain': ['Web3.js', 'MetaMask integration', 'Smart contract interaction'],
    'State & Data Management': ['Redux', 'MobX', 'Easy Peasy', 'React Query (TanStack Query)', 'Axios'],
    'Testing': ['Cypress (E2E)', 'Jest', 'React Testing Library'],
    'Back-End Development': ['PHP', 'Laravel'],
    'Database Management': ['MySQL', 'MS SQL Server'],
    'Version Control & Collaboration': ['Git', 'GitHub', 'GitLab', 'Bitbucket'],
    'Project Management': ['Jira', 'Trello'],
    'DevOps & Deployment': ['Docker', 'CI/CD pipelines', 'GitHub Actions', 'Netlify']
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="max-w-6xl mx-auto px-2">
          {/* Personal Section with Photo */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-16"
          >
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
              {/* Text Content */}
              <div className="flex-1 space-y-6">
                <div>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2">
                    Hey <span className="text-primary-500">(:</span>
                  </h2>
                  <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                    I'm Shejza <span className="wave">👋</span>
                  </h3>
                </div>
                
                <div className="space-y-4 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  <p>
                    I have over six years of experience creating and styling websites and applications. I specialize in coding modular, component-based websites and building modern Web3 solutions.
                  </p>
                  <p>
                    I am passionate about clean, maintainable, readable code and I fight the good fight against software entropy. I believe in creating pixel-perfect user interfaces that deliver exceptional user experiences.
                  </p>
                  <p>
                    Interested in the entire frontend spectrum and working on ambitious projects with positive people. When I'm not coding, you can find me hiking in the mountains, playing tennis, or dancing salsa.
                  </p>
                </div>
              </div>
              
              {/* Photo */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-64 h-64 rounded-2xl overflow-hidden transform rotate-3">
                    <div className="w-full h-full object-cover overflow-hidden bg-white">
                      <img  
                        src={shejzaImage} 
                        alt="Shejza Berisha" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Skills */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Skills & Technologies</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {Object.entries(skills).map(([category, skillList], index) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white dark:bg-custom-800 rounded-lg p-6 border border-gray-200/40 dark:border-custom-700/40"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {skillList.map((skill) => (
                      <span key={skill} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Experience */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Work Experience</h2>
            <div className="space-y-8">
              {experience.map((exp, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white dark:bg-custom-800 rounded-lg p-6 border border-gray-200/40 dark:border-custom-700/40"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{exp.title}</h3>
                      <p className="text-primary-600 dark:text-primary-400 font-medium">{exp.company}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{exp.location} • {exp.period}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{exp.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About 